import React from 'react';
import Head from 'next/head';
import { Config, I18nContext } from '@deporvillage/utils-library-component';
import {
    CriteoOneTag,
    FacebookPixel,
    Optimize,
    OneTrust,
    Sfmc,
    TrackingLibrary,
    PixelCampaign,
} from '@deporvillage/frontend-common';

const ItemsPerPage = 42;

const Robots = {
    IndexFollow: 'index,follow',
    NoIndexNoFollow: 'noindex,nofollow',
};

const capitalize = (s) => s?.replace(/^\w/, (c) => c.toUpperCase());
const getStoreUrl = (stores, lang) => stores.find((s) => s.lang === lang)?.url;

const getTitle = ({
    metaTitle, title, name, page,
}, translate) => {
    if (metaTitle) {
        return translate(`meta_data.category.title${page > 1 ? '_pagination' : ''}`, {
            name: capitalize((() => {
                if (title !== name) return title;
                return metaTitle;
            })()),
            page,
        });
    }

    return translate(`meta_data.category.${(() => {
        if (page > 1) return 'title_pagination';
        if (title !== name) return 'title';
        return 'default_subcategory_title';
    })()}`, {
        name: capitalize(title),
        page,
    });
};

const getRobots = (country, store, searchRobots) => {
    const isNoIndexNoFollow = country.currency_code !== store.currency_code;
    if (searchRobots && !isNoIndexNoFollow) return searchRobots;
    return isNoIndexNoFollow ? Robots.NoIndexNoFollow : Robots.IndexFollow;
};

const getTrackingConfig = ({
    tracking,
    ebus,
    optimize,
    environment,
}) => ({
    analytics: {
        name: 'Analytics',
        config: {
            analytics: {
                version: tracking?.analytics?.version,
                code: tracking?.analytics?.code,
            },
            ga4: {
                code:
                    ebus?.providers?.ga4?.config &&
                    ebus.providers.ga4.config[environment]?.code,
                como:
                    (ebus?.providers?.ga4?.config &&
                        ebus?.providers?.ga4?.config[environment]?.como) ||
                    null,
            },
            adwords: {
                code: tracking?.adwords?.code,
            },
            optimize: {
                enabled: optimize.enabled,
                code: optimize.code,
            },
            tagManager: {},
        },
    },
    deporvillage: {
        name: ebus?.providers?.deporvillage?.name,
        config: ebus?.providers?.deporvillage?.config[environment],
    },
});

const CategoryTemplate = ({ children, model }) => {
    const {
        name,
        metaTitle,
        canonical,
        url: categoryUrl,
        breadcrumb,
        pagination,
        rootCategory,
        searchRobots,
        searchLabel,
    } = model;

    const title = searchLabel ? `${name} ${searchLabel}` : name;

    const {
        lang, ebus, environment, oneTrust, tracking, optimize, facebook, pixelCampaign,
        page, sfmc, store, storeId, country, url, criteo, locale, extension, stores,
    } = React.useContext(Config);

    const { translate } = React.useContext(I18nContext);

    const trackingProviders = getTrackingConfig({
        tracking,
        ebus,
        optimize,
        environment,
    });

    const lastPage = Math.ceil(pagination?.itemsCount / ItemsPerPage);
    const canonicalUrl = `${url}${(canonical && canonical.find((c) => c.lang === lang)?.url) || categoryUrl}${pagination?.current > 1 ? `?p=${pagination.current}` : ''}`;
    const robotsData = getRobots(country, store, searchRobots);

    const categoryTitle = getTitle({
        metaTitle, title, name, page: pagination?.current,
    }, translate);

    const categoryDescription = translate(
        `meta_data.category.description${pagination?.current > 1 ? '_pagination' : ''}`,
        {
            name: name?.toLowerCase(),
            name_upper: capitalize(name),
            domain_extension: extension.replace('.', ''),
            page: pagination?.current || 0,
        },
    );

    const pixelCampaignCategory = pixelCampaign?.enabled &&
        pixelCampaign?.page?.categoryPage?.find(
            ({ id: pId }) => +pId === rootCategory?.id,
        )?.category;

    return (
        <>
            <Head>
                <title key={'title'}>{categoryTitle}</title>
                <meta name='description' content={categoryDescription} />

                <link rel="schema.DC" href="http://purl.org/dc/elements/1.1/" type="text/plain" />
                <link rel="schema.DCTERMS" href="http://purl.org/dc/terms/" type="text/plain" />
                <meta name='DC.title' lang={lang} content={categoryTitle} />
                <meta name='DC.description' lang={lang} content={categoryDescription} />
                {/* eslint-disable-next-line react/no-unknown-property */}
                <meta name='DC.identifier' scheme='DCTERMS.URI' content={`https:${canonicalUrl}`} />
                {/* eslint-disable-next-line react/no-unknown-property */}
                <meta name='DC.type' scheme='DCTERMS.DCMIType' content='Text' />
                <meta name='DC.format' content='text/html; charset=utf-8' />
                {/* eslint-disable-next-line react/no-unknown-property */}
                <meta name='DC.language' scheme='ISO639-1' content={lang} />

                <meta property='og:title' content={categoryTitle} />
                <meta property='og:description' content={categoryDescription} />
                <meta property='og:locale' content={locale} />
                <meta property='og:site_name' content={`deporvillage${extension}`} />
                <meta property='og:url' content={`https:${canonicalUrl}`} />

                {
                    canonical &&
                    (!pagination || pagination?.itemsCount) &&
                    (robotsData !== Robots.NoIndexNoFollow) && (
                        <link
                            rel='canonical'
                            href={`https:${canonicalUrl}`}
                        />
                    )
                }

                {
                    (!pagination || pagination?.current === 1) &&
                    (robotsData !== Robots.NoIndexNoFollow) &&
                    (canonical?.map(({ url, lang }) => (
                        <link
                            key={lang}
                            rel='alternate'
                            href={`https:${getStoreUrl(stores, lang)}${url}`}
                            hrefLang={lang}
                        />
                    )))
                }

                {
                    pagination?.current > 1 && (
                        <link
                            rel={'prev'}
                            href={`https:${url}${categoryUrl}?p=${pagination.current - 1}`}
                        />
                    )
                }

                {
                    pagination?.current < lastPage && (
                        <link
                            rel={'next'}
                            href={`https:${url}${categoryUrl}?p=${pagination.current + 1}`}
                        />
                    )
                }

                <meta name='robots' content={robotsData} />

            </Head>

            {
                optimize && (
                    <Optimize code={optimize.code} />
                )
            }

            {
                oneTrust && oneTrust.enabled && (
                    <OneTrust
                        id={oneTrust.id}
                        lang={lang}
                        script={oneTrust.script}
                    />
                )
            }

            {
                facebook && facebook.enabled && (
                    <FacebookPixel
                        page={page}
                        currency={country.currency_code}
                    />
                )
            }

            {
                criteo && criteo.enabled && (
                    <CriteoOneTag
                        id={criteo.accountId}
                        script={criteo.script}
                        page={page}
                        model={model}
                    />
                )
            }

            {
                sfmc && sfmc.enabled && sfmc?.originId && (
                    <Sfmc id={sfmc.originId} model={model} page={page} />
                )
            }

            {
                ebus?.bucket && (
                    <TrackingLibrary
                        providers={trackingProviders}
                        bucket={ebus.bucket[environment]}
                        breadcrumb={breadcrumb}
                        storeId={storeId}
                    />
                )
            }

            {
                pixelCampaignCategory && (
                    <PixelCampaign category={pixelCampaignCategory} />
                )
            }

            {children}
        </>
    );
};

export default CategoryTemplate;
